import { ArrowRight, FileText, Loader2, Phone, TrendingUp, Users, Video } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHorizontalMarquee } from '../../../hooks/useHorizontalMarquee';
import {
  fetchApprovedTestimonials,
  fetchPlatformPublicStats,
  formatProofNumber,
  pickSuccessStories,
  type PlatformPublicStats,
  type SuccessStory,
} from '../../../services/platformSocialProofService';

import StoryStarRating from './StoryStarRating';

interface HomeSocialProofSectionProps {
  onNavigate?: (view: string) => void;
  /** Cap stories on homepage funnel; omit for full wall-of-love page */
  maxStories?: number;
  layout?: 'grid' | 'marquee';
  showCta?: boolean;
  showViewAllLink?: boolean;
}

const MARQUEE_SPEED_PX_PER_SEC = 42;

function MetricCard({
  icon: Icon,
  value,
  label,
  accent,
}: {
  icon: React.FC<{ className?: string }>;
  value: string;
  label: string;
  accent: string;
}) {
  return (
    <div className="home-social-proof__metric">
      <div className="home-social-proof__metric-icon" style={{ color: accent }}>
        <Icon className="w-4 h-4" aria-hidden />
      </div>
      <p className="home-social-proof__metric-value t-num">{value}</p>
      <p className="home-social-proof__metric-label">{label}</p>
    </div>
  );
}

function SuccessStoryMarqueeCard({ story }: { story: SuccessStory }) {
  const { t } = useTranslation();
  const showBeforeAfter = Boolean(story.beforeValue && story.afterValue);

  return (
    <article className="home-social-proof__marquee-card">
      <div className="home-social-proof__marquee-card-top">
        <div className="home-social-proof__marquee-card-avatar" aria-hidden>
          <Phone className="w-4 h-4" />
        </div>
        <div className="home-social-proof__marquee-card-head">
          <p className="home-social-proof__story-name">{story.name}</p>
          <p className="home-social-proof__story-role">
            {story.role}
            {story.location ? ` · ${story.location}` : ''}
          </p>
        </div>
        {story.source === 'verified' ? (
          <span className="home-social-proof__verified-badge">
            {t('home.socialProof.verified', 'Verified')}
          </span>
        ) : (
          <span className="home-social-proof__rep-badge">
            {t('home.socialProof.representative', 'Early user')}
          </span>
        )}
      </div>

      <StoryStarRating rating={story.rating} />

      {showBeforeAfter ? (
        <div className="home-social-proof__before-after home-social-proof__before-after--compact">
          <div className="home-social-proof__ba-col home-social-proof__ba-col--before">
            <span className="home-social-proof__ba-label">{story.beforeLabel}</span>
            <span className="home-social-proof__ba-value">{story.beforeValue}</span>
          </div>
          <ArrowRight className="w-3.5 h-3.5 shrink-0 home-social-proof__ba-arrow" aria-hidden />
          <div className="home-social-proof__ba-col home-social-proof__ba-col--after">
            <span className="home-social-proof__ba-label">{story.afterLabel}</span>
            <span className="home-social-proof__ba-value home-social-proof__ba-value--after">
              {story.afterValue}
            </span>
          </div>
        </div>
      ) : null}

      {story.quote ? (
        <blockquote className="home-social-proof__quote home-social-proof__quote--marquee">
          &ldquo;{story.quote}&rdquo;
        </blockquote>
      ) : null}
    </article>
  );
}

function StoryMarqueeGroup({
  stories,
  groupId,
  ariaHidden = false,
}: {
  stories: SuccessStory[];
  groupId: string;
  ariaHidden?: boolean;
}) {
  return (
    <div className="home-social-proof__marquee-group" aria-hidden={ariaHidden || undefined}>
      {stories.map((story) => (
        <span key={`${groupId}-${story.id}`} className="home-social-proof__marquee-item">
          <SuccessStoryMarqueeCard story={story} />
        </span>
      ))}
    </div>
  );
}

function SuccessStoryGridCard({ story }: { story: SuccessStory }) {
  return (
    <article className="home-social-proof__grid-card">
      <SuccessStoryMarqueeCard story={story} />
    </article>
  );
}

const HomeSocialProofSection: React.FC<HomeSocialProofSectionProps> = ({
  onNavigate,
  maxStories,
  layout = 'grid',
  showCta = true,
  showViewAllLink = false,
}) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<PlatformPublicStats | null>(null);
  const [stories, setStories] = useState<SuccessStory[]>([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const [platformStats, testimonials] = await Promise.all([
        fetchPlatformPublicStats(),
        fetchApprovedTestimonials(12),
      ]);
      if (!mounted) return;
      setStats(platformStats);
      const picked = pickSuccessStories(testimonials);
      setStories(maxStories ? picked.slice(0, maxStories) : picked);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [maxStories]);

  useHorizontalMarquee(trackRef, marqueeRef, layout === 'marquee' && !loading && stories.length > 0, {
    groupSelector: '.home-social-proof__marquee-group',
    trackActiveClass: 'home-social-proof__marquee-track--js',
    speedPxPerSec: MARQUEE_SPEED_PX_PER_SEC,
  });

  const hasLiveStats = stats && stats.totalUsers > 0;
  const hasInterviewStats = Boolean(stats && stats.interviewsLogged > 0);

  const metrics = hasLiveStats
    ? [
        {
          icon: Users,
          value: formatProofNumber(stats.totalUsers),
          label: t('home.socialProof.metricUsers', 'Job seekers on GoGrabJob'),
          accent: 'var(--ds-marketing-accent, #5F8568)',
        },
        {
          icon: Phone,
          value: hasInterviewStats
            ? formatProofNumber(stats.interviewsLogged)
            : t('home.socialProof.metricRecruiterResponsesValue', '3.5x'),
          label: hasInterviewStats
            ? t('home.socialProof.metricInterviews', 'Interview calls logged')
            : t('home.socialProof.metricRecruiterResponses', 'More recruiter responses'),
          accent: '#6366f1',
        },
        {
          icon: TrendingUp,
          value: stats.avgAtsImprovementPct > 0
            ? `${stats.avgAtsImprovementPct}%`
            : '—',
          label: t('home.socialProof.metricAts', 'Avg. ATS score improvement'),
          accent: '#22c55e',
        },
        {
          icon: FileText,
          value: formatProofNumber(stats.resumesOptimized),
          label: t('home.socialProof.metricOptimized', 'Resumes optimized'),
          accent: '#f59e0b',
        },
      ]
    : [];

  return (
    <section id="social-proof" className="home-social-proof" aria-labelledby="home-social-proof-title">
      <div className="home-social-proof__inner">
        <p className="home-social-proof__eyebrow">
          {t('home.socialProof.eyebrow', 'Interview calls, not empty promises')}
        </p>
        <h2 id="home-social-proof-title" className="home-social-proof__title">
          {t('home.socialProof.title', 'More recruiter calls — that is what we help with')}
        </h2>
        <p className="home-social-proof__lede">
          {t(
            'home.socialProof.lede',
            'GoGrabJob does not place you in a job. We help you fix ATS gaps, tailor faster, and turn applications into interview calls.',
          )}
        </p>

        {hasLiveStats ? (
          <div className="home-social-proof__metrics" role="list">
            {metrics.map((m) => (
              <MetricCard key={m.label} {...m} />
            ))}
          </div>
        ) : (
          <p className="home-social-proof__stats-pending t-meta">
            {t('home.socialProof.statsLoading', 'Platform metrics load from live usage data.')}
          </p>
        )}

        <h3 className="home-social-proof__stories-title">
          {t('home.socialProof.storiesTitle', 'Job seekers getting interview calls')}
        </h3>

        {loading ? (
          <div className="home-social-proof__loading" aria-busy="true">
            <Loader2
              className="w-6 h-6 animate-spin"
              style={{ color: 'var(--ds-marketing-accent, #5F8568)' }}
            />
            <span className="t-meta">{t('home.socialProof.loading', 'Loading success stories…')}</span>
          </div>
        ) : layout === 'grid' ? (
          <div className="home-social-proof__grid" role="list">
            {stories.map((story) => (
              <SuccessStoryGridCard key={story.id} story={story} />
            ))}
          </div>
        ) : (
          <div ref={marqueeRef} className="home-social-proof__marquee">
            <div className="home-social-proof__marquee-viewport">
              <div
                ref={trackRef}
                className="home-social-proof__marquee-track"
                role="region"
                aria-label={t('home.socialProof.carouselLabel', 'Scrolling interview success stories')}
              >
                <StoryMarqueeGroup stories={stories} groupId="a" />
                <StoryMarqueeGroup stories={stories} groupId="b" ariaHidden />
              </div>
            </div>
          </div>
        )}

        <p className="home-social-proof__disclaimer">
          {t(
            'home.socialProof.disclaimer',
            'We help job seekers improve applications and get interview calls — we do not guarantee job offers. Individual results vary. Verified stories are approved by our team.',
          )}
        </p>

        {showViewAllLink && onNavigate ? (
          <div className="home-social-proof__cta-row">
            <button
              type="button"
              className="home-social-proof__cta home-social-proof__cta--secondary"
              onClick={() => onNavigate('success-stories')}
            >
              {t('home.socialProof.viewAll', 'Read all success stories')}
              <ArrowRight className="w-4 h-4" aria-hidden />
            </button>
          </div>
        ) : null}

        {showCta && onNavigate ? (
          <div className="home-social-proof__cta-row">
            <button type="button" className="home-social-proof__cta" onClick={() => onNavigate('signup')}>
              {t('home.socialProof.cta', 'Start free — get more interview calls')}
              <ArrowRight className="w-4 h-4" aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default HomeSocialProofSection;
